import type { PaymentFlowStore, PaymentRetryInfo } from '../../../state/payment-stores/payment-flow-store';

import { getPlaceOrderButtonText } from './get-place-order-button-text';
import { isCommitFailureRetry, isRetryablePaymentError } from './payment-retry-utils';

export type PlaceOrderFlowState = PaymentFlowStore['currentState'];

/** Delivery quote is still in flight — a fee may still arrive. */
const DELIVERY_QUOTE_REQUESTED = 'QUOTE_REQUESTED';

export interface PlaceOrderButtonStateInput {
  /** `serverOrder.status` from the GetOrder poll. */
  serverOrderStatus?: string;
  /** Whether `serverOrder.delivery` is present at all. */
  hasDeliveryQuote: boolean;
  /** `serverOrder.delivery.status`, when the quote object exists. */
  deliveryQuoteStatus?: string;
  serverOrderLoading: boolean;
  /** GetOrder poll resolved to a failure status (PRICE_ERROR, QUOTE_ERROR, …). */
  serverOrderError: boolean;
  /** The PriceOrder mutation itself threw — there is no rbiOrderId and no serverOrder. */
  hasPriceMutationError: boolean;

  currentState: PlaceOrderFlowState;
  currentErrorCode?: string;
  hasCurrentError: boolean;
  retryInfo: PaymentRetryInfo | null;
  isCommitRetry: boolean;
  isOfflineDuringCommit: boolean;
  pendingCommitRetry: boolean;
  isProcessing: boolean;

  isDelivery: boolean;
  isPaymentReady: boolean;
  isOfferValidationError: boolean;
  offersLoading: boolean;
  cartEntryCount: number;
  hasReachedMaxRedemptionLimit: boolean;
  rewardTierApplied: boolean;
  isTipAmountInvalid: boolean;

  /** Latch owned by the component across renders; see `confirmingStarted` on the result. */
  confirmingStarted: boolean;
}

export interface PlaceOrderButtonState {
  isButtonLoading: boolean;
  isButtonDisabled: boolean;
  buttonText: string;
  isAwaitingUserRetry: boolean;
  /** Pricing failed and a reprice is the offered action — press should call tryPricing(). */
  isAwaitingPricingRetry: boolean;
  isConfirmingSubmittedOrder: boolean;
  hasRetryableFailure: boolean;
  needsCommitReprice: boolean;
  retryErrorCode?: string;
  hasPricingFailure: boolean;
  isDeliveryFeesLoading: boolean;
  isMissingDeliveryQuote: boolean;
  showPricingAlert: boolean;
  showPaymentError: boolean;
  /** Next value for the caller's latch ref. */
  confirmingStarted: boolean;
}

export function getPlaceOrderButtonState(
  input: PlaceOrderButtonStateInput
): PlaceOrderButtonState {
  const {
    serverOrderStatus,
    hasDeliveryQuote,
    deliveryQuoteStatus,
    serverOrderLoading,
    serverOrderError,
    hasPriceMutationError,
    currentState,
    currentErrorCode,
    hasCurrentError,
    retryInfo,
    isCommitRetry,
    isOfflineDuringCommit,
    pendingCommitRetry,
    isProcessing,
    isDelivery,
    isPaymentReady,
    isOfferValidationError,
    offersLoading,
    cartEntryCount,
    hasReachedMaxRedemptionLimit,
    rewardTierApplied,
    isTipAmountInvalid,
    confirmingStarted,
  } = input;

  const serverOrderProcessing =
    serverOrderLoading ||
    serverOrderStatus === 'PRICE_REQUESTED' ||
    serverOrderStatus === 'INSERT_REQUESTED' ||
    serverOrderStatus === 'FIRE_REQUESTED';

  // Pipeline is processing when currentState indicates active processing
  // (e.g., when handlePlaceOrder is called from gift card reload action sheet)
  const isPipelineProcessing =
    currentState !== 'idle' && currentState !== 'success' && currentState !== 'error';

  const hasValidOrderForCommit =
    serverOrderStatus === 'PRICE_SUCCESSFUL' ||
    serverOrderStatus === 'PAYMENT_ERROR' ||
    serverOrderStatus === 'VALIDATION_ERROR';

  const retryErrorCode = retryInfo?.errorCode ?? currentErrorCode;
  const hasRetryableFailure = !!retryInfo || isRetryablePaymentError(currentErrorCode);
  const needsCommitReprice = isCommitFailureRetry(retryErrorCode);
  const canPlaceOrder = hasValidOrderForCommit || hasRetryableFailure;

  // A failed price never produces a delivery quote, so a bare `!serverOrder.delivery`
  // presence check latches on forever. Split the two questions apart:
  //   - can we place the order?  -> is the quote missing
  //   - should we show a spinner? -> can a quote still arrive
  const hasPricingFailure = serverOrderError || hasPriceMutationError;
  const isMissingDeliveryQuote = isDelivery && !hasDeliveryQuote;
  const isDeliveryQuoteInFlight = isDelivery && deliveryQuoteStatus === DELIVERY_QUOTE_REQUESTED;
  const isDeliveryFeesLoading =
    !hasPricingFailure &&
    (isDeliveryQuoteInFlight || (isMissingDeliveryQuote && serverOrderProcessing));

  const isAwaitingUserRetry =
    hasRetryableFailure &&
    (currentState === 'error' || hasCurrentError) &&
    !pendingCommitRetry &&
    !isProcessing;

  const isButtonLoading =
    !isAwaitingUserRetry &&
    (isProcessing ||
      serverOrderProcessing ||
      isPipelineProcessing ||
      offersLoading ||
      isDeliveryFeesLoading);

  let nextConfirmingStarted = confirmingStarted;
  if (currentState === 'confirming' || (currentState === 'committing' && isOfflineDuringCommit)) {
    nextConfirmingStarted = true;
  } else if (hasCurrentError || currentState === 'idle' || currentState === 'success') {
    nextConfirmingStarted = false;
  }

  const isHandoffFromConfirming =
    nextConfirmingStarted && !hasCurrentError && currentState === 'error';

  const isConfirmingSubmittedOrder =
    !pendingCommitRetry &&
    (!isCommitRetry || isOfflineDuringCommit) &&
    (currentState === 'confirming' ||
      isOfflineDuringCommit ||
      isHandoffFromConfirming ||
      (serverOrderStatus === 'INSERT_REQUESTED' &&
        (currentState === 'error' || hasCurrentError)));

  // Gates a reprice cannot clear — these keep the button disabled regardless.
  const hasBlockingCartState =
    isOfferValidationError ||
    !isPaymentReady ||
    cartEntryCount === 0 ||
    (hasReachedMaxRedemptionLimit && rewardTierApplied) ||
    isTipAmountInvalid;

  // A pricing failure is recoverable by re-pricing, so offer that as the action
  // rather than leaving a dead disabled button under the alert.
  const isAwaitingPricingRetry =
    hasPricingFailure &&
    !hasRetryableFailure &&
    !hasBlockingCartState &&
    !isConfirmingSubmittedOrder &&
    !isProcessing &&
    !isPipelineProcessing &&
    !serverOrderProcessing;

  const isButtonDisabled =
    isOfferValidationError ||
    !isPaymentReady ||
    (!isAwaitingUserRetry &&
      !isAwaitingPricingRetry &&
      (isProcessing ||
        serverOrderProcessing ||
        isPipelineProcessing ||
        isMissingDeliveryQuote ||
        isDeliveryFeesLoading)) ||
    (!canPlaceOrder && !isAwaitingPricingRetry) ||
    cartEntryCount === 0 ||
    (hasPricingFailure &&
      !isConfirmingSubmittedOrder &&
      !hasRetryableFailure &&
      !isAwaitingPricingRetry) ||
    (hasReachedMaxRedemptionLimit && rewardTierApplied) ||
    isTipAmountInvalid;

  const buttonFlowState: PlaceOrderFlowState =
    (isCommitRetry || pendingCommitRetry) && currentState === 'confirming'
      ? 'committing'
      : currentState === 'committing' &&
          isOfflineDuringCommit &&
          !isCommitRetry &&
          !pendingCommitRetry
        ? 'confirming'
        : currentState;

  const buttonText = getPlaceOrderButtonText({
    currentState: buttonFlowState,
    hasRetryableFailure,
    isPricingRetry: isAwaitingPricingRetry,
    isPricingLoading:
      serverOrderLoading || isDeliveryFeesLoading || serverOrderStatus === 'PRICE_REQUESTED',
    hasPricingFailure,
    serverOrderStatus,
  });

  // Hold pricing/payment errors while INSERT_REQUESTED — the poll is still confirming the order.
  const showPricingAlert =
    hasPricingFailure && !isPipelineProcessing && !isConfirmingSubmittedOrder;
  const showPaymentError = hasCurrentError && !isConfirmingSubmittedOrder;

  return {
    isButtonLoading,
    isButtonDisabled,
    buttonText,
    isAwaitingUserRetry,
    isAwaitingPricingRetry,
    isConfirmingSubmittedOrder,
    hasRetryableFailure,
    needsCommitReprice,
    retryErrorCode,
    hasPricingFailure,
    isDeliveryFeesLoading,
    isMissingDeliveryQuote,
    showPricingAlert,
    showPaymentError,
    confirmingStarted: nextConfirmingStarted,
  };
}
